import * as d3 from "d3";
import { style } from "d3";

export default {
    methods: {
        // Only nodes with top n highest values
        filterTopNodes(nodes, edges, n) {
            // Sort nodes by value
            let nodesSorted = nodes.sort(function (nodeA, nodeB) {
                return nodeB.value - nodeA.value;
            });

            // Keep top n nodes
            let nodesTopN = nodesSorted.slice(0, n);
            let nodesTopN_ids = nodesTopN.map(function (node) {
                return node.id;
            });

            // Find edges which source and target nodes
            // are among the top n nodes
            let edgesTopN = edges.filter(
                (d) =>
                    nodesTopN_ids.indexOf(d.source) > -1 &&
                    nodesTopN_ids.indexOf(d.target) > -1
            );

            return [nodesTopN, edgesTopN];
        },

        drawGraph(
            nodesToDraw,
            edgesToDraw,
            selected,
            // Options
            {
                containerElement,
                chargeStrength = -40,
                forceDistanceMax = 500,
                nodeLabel = (d) => d.name,
                nodeLabelFontSize,
                nodeRadius = (d) => d.value,
                nodeFillColorScale,
                linkStrokeOpacity = (l) => l.value,
                linkStrokeWidth = (l) => l.value,
                linkStrokeHighlighted = "#1a5fb4",
                linkStroke = "#999"
            } = {}
        ) {

            // Copy nodes into a hash table
            let nodeHash = {};
            nodesToDraw.forEach((node) => {
                nodeHash[node.id] = node;
            });

            // Use actual node objects
            // as edge source and target
            edgesToDraw.forEach((edge) => {
                edge.weight = parseInt(edge.value);
                edge.source = nodeHash[edge.source];
                edge.target = nodeHash[edge.target];
            });

            // Init link force
            let linkForce = d3.forceLink();

            // Append emtpy svg canvas
            containerElement.select("svg").remove();
            let svg = containerElement
                .append("svg")
                .attr("width", "100%")
                .attr("height", "100%");
            //.attr("viewBox", [-450, -450, 900, 900]);


            // Init SVG containerm used for
            // Zooming and Panning
            const container = svg.append('g')
                .classed('container', true);


            // Init simulation
            let simulation = d3
                .forceSimulation()
                .force(
                    "charge",
                    d3
                        .forceManyBody()
                        .strength(chargeStrength) // reduce charge of nodes
                        .distanceMax(forceDistanceMax) // reduce accuracy and cpu load
                )
                .force("center", d3.forceCenter())
                .force("link", linkForce) // attach link charge to egdes
                .nodes(nodesToDraw)
                .on("tick", forceTick); // event used by forceSimulation to update node positions

            simulation.force("link").links(edgesToDraw);


            let linkEnter = container
                .selectAll("link.link-line")
                .data(edgesToDraw, (d) => `${d.source.id}-${d.target.id}`)
                .enter()
                .append("g")
                .attr("class", "link")
                .append("line")
                .attr("class", "link-line")
                .attr("stroke", linkStroke)
                .style("opacity", linkStrokeOpacity)
                .style("stroke-width", linkStrokeWidth);

            let linkEnterWide = svg.selectAll(".link")
                .append("line")
                .attr("class", "link-line-wide")
                .attr("stroke", linkStrokeHighlighted)
                .style("opacity", 0)
                .style("stroke-width", 3);

            let labelLine = svg.selectAll(".link")
                .append("text")
                .attr("class", "link-label")
                .attr("fill", "Black")
                .style("font-size", "5px")
                .style("opacity", 0)
                .attr("text-anchor", "middle")
                .text((d) => `${Math.round(d.value * 100) / 100}`);

            let nodeEnter = container
                .selectAll("g.node")
                .data(nodesToDraw, (d) => d.id)
                .enter()
                .append("g")
                .attr("class", "node");

            nodeEnter
                .append("circle")
                .attr("class", "node-circle")
                .attr("r", nodeRadius)
                .style("stroke", "#fff")
                .style("stroke-width", 1)
                .style("fill", (d) => nodeFillColorScale(d.group)); // assign color based on entity type

            nodeEnter
                .append("text")
                .style("text-anchor", "middle")
                .attr("dy", 20)
                .style("font-size", nodeLabelFontSize)
                .style("color", "#1e1e2f")
                .text(nodeLabel); // define label

            // ############### Draw Legend ###############

            //Drawing the legend
            const legend_g = svg.selectAll(".legend")
                .data(nodeFillColorScale.domain())
                .enter().append("g")
                .attr("transform", (d, i) => `translate(${10},${i * 20})`);

            legend_g.append("circle")
                .attr("cx", 0)
                .attr("cy", 0)
                .attr("r", 5)
                .attr("fill", nodeFillColorScale);

            legend_g.append("text")
                .attr("x", 10)
                .attr("y", 5)
                .attr('font-size', 10)
                .text(d => d);

            // ############### Zoom Graph ###############

            const onZoom = (container, event) => {
                container.attr('transform', event.transform);
            }

            const zoom = d3.zoom()
                .scaleExtent([0.5, 10])
                .on('zoom', e => onZoom(container, e));
            svg.call(zoom);

            // Center graph within SVG    
            let svgHeight = svg.style("height").replace("px", "");
            let svgWidth = svg.style("width").replace("px", "");

            const centered = d3.zoomIdentity
                .translate(svgWidth / 2, svgHeight / 2);
            svg.call(zoom.transform, centered);

            // ############### Node Details Tooltip ###############

            // Select tooltip
            const tooltip = d3.select("#tooltip-node");

            // Make toooltip visible
            nodeEnter.on("click", function (event, d) {

                selected.node = d // remember clicked node 

                const tooltipWidth = tooltip.node().offsetWidth;
                const windowWidth = window.innerWidth;
                const offsetTop = event.pageY;
                const offsetLeft = event.pageX;

                let top;
                let left;

                if (offsetLeft + tooltipWidth <= windowWidth - 20) {
                    top = offsetTop + 10;
                    left = offsetLeft;
                } else {
                    top = offsetTop + 10;
                    left = offsetLeft - tooltipWidth;
                }

                tooltip
                    .style("z-index", 999)
                    .style("left", `${left}px`)
                    .style("top", `${top}px`);

                tooltip.transition().duration(200).style("opacity", 1);
            });


            // Make toooltip visible
            const tooltipCloseButton = d3.select("#tooltip-node-button-close");

            tooltipCloseButton.on("click", function () {
                tooltip.style("z-index", -1);
                tooltip.transition().duration(200).style("opacity", 0);
            });

            // ############### Edge Details Tooltip ###############

            // Select tooltip
            const tooltipEdge = d3.select("#tooltip-edge");

            // Make toooltip visible
            linkEnterWide.on("click", function (event, d) {

                selected.edge = d // remember clicked edge

                const tooltipWidth = tooltipEdge.node().offsetWidth;
                const windowWidth = window.innerWidth;
                const offsetTop = event.pageY;
                const offsetLeft = event.pageX;


                let top;
                let left;

                if (offsetLeft + tooltipWidth <= windowWidth - 20) {
                    top = offsetTop + 10;
                    left = offsetLeft;
                } else {
                    top = offsetTop + 10;
                    left = offsetLeft - tooltipWidth;
                }

                tooltipEdge
                    .style("z-index", 999)
                    .style("left", `${left}px`)
                    .style("top", `${top}px`);

                tooltipEdge.transition().duration(200).style("opacity", 1);
            });

            // Make toooltip visible
            const tooltipCloseButtonEdge = d3.select("#tooltip-edge-button-close");

            tooltipCloseButtonEdge.on("click", function () {
                tooltipEdge.style("z-index", -1);
                tooltipEdge.transition().duration(200).style("opacity", 0);
            });


            // ############### Highlight Edge ###############

            linkEnterWide.on("mouseover", function (l) {
                d3.select(this).style("opacity", 1)
            });

            linkEnterWide.on("mouseleave", function (l) {
                d3.select(this).style("opacity", 0)
            });

            // ############### Highlight connected Edges ###############

            nodeEnter.on("mouseover", function (event, d) {
                const id = d.id;
                linkEnter.style("stroke", function (l) {
                    if (id === l.source.id || id === l.target.id)
                        return linkStrokeHighlighted;
                    else return linkStroke;
                });

                linkEnter.style("opacity", function (l) {
                    if (id === l.source.id || id === l.target.id) return 0.9;
                    else return 0.1;
                });

                labelLine.style("opacity", function (l) {
                    if (id === l.source.id || id === l.target.id) return 0.9;
                    else return 0;
                });
            });

            nodeEnter.on("mouseleave", function (d) {
                linkEnter.style("stroke", linkStroke);
                linkEnter.style("opacity", linkStrokeOpacity);
                labelLine.style("opacity", 0)
            });

            // ############### Drag Node ###############

            let drag = d3.drag();
            drag.on("drag", dragging);
            svg.selectAll("g.node").call(drag);

            function dragging(event) {
                event.subject.fx = event.x;
                event.subject.fy = event.y;
                if (simulation.alpha() < 0.1) {
                    simulation.alpha(0.1);
                    simulation.restart();
                }
            }

            // ############### Advance Simulation ###############

            function forceTick() {

                linkEnter
                    .attr("x1", (d) => d.source.x)
                    .attr("x2", (d) => d.target.x)
                    .attr("y1", (d) => d.source.y)
                    .attr("y2", (d) => d.target.y);

                linkEnterWide.attr("x1", (d) => d.source.x)
                    .attr("x2", (d) => d.target.x)
                    .attr("y1", (d) => d.source.y)
                    .attr("y2", (d) => d.target.y);

                nodeEnter
                    .attr("transform", (d) => `translate(${d.x},${d.y})`);

                labelLine
                    .attr("x", function (d) {
                        return (d.source.x + d.target.x) / 2;
                    })
                    .attr("y", function (d) {
                        return (d.source.y + d.target.y) / 2;
                    });
            }

            this.graph.simulation = simulation;
        },

        filterNetwork() {
            let filter = this.menues.options.options;
            //this.simulation.stop();
            let simulation = this.graph.simulation;
            let originalNodes = simulation.nodes();
            let originalLinks = simulation.force("link").links();

            let influentialNodes = originalNodes.filter(
                (d) =>
                    !filter.typesSelected.includes(d.group) || d.value < filter.nodeThreshold
            );
            let influentialLinks = originalLinks.filter(
                (d) =>
                ((influentialNodes.indexOf(d.source) > -1 ||
                    influentialNodes.indexOf(d.target) > -1) ||
                    d.value < filter.edgeThreshold)
            );

            d3.selectAll("g.node")
                .style("visibility", "visible");


            d3.selectAll("text.link-label")
                .style("visibility", "visible")
                .attr("pointer-events", "all");

            d3.selectAll("line.link-line")
                .style("visibility", "visible")
                .attr("pointer-events", "all");

            d3.selectAll("line.link-line-wide")
                .style("visibility", "visible")
                .attr("pointer-events", "all");

            d3.selectAll("g.node")
                .data(influentialNodes, (d) => d.id)
                .style("visibility", "hidden");

            d3.selectAll("text.link-label")
                .data(influentialLinks, (d) => `${d.source.id}-${d.target.id}`)
                .style("visibility", "hidden")
                .attr("pointer-events", "none");

            d3.selectAll("line.link-line")
                .data(influentialLinks, (d) => `${d.source.id}-${d.target.id}`)
                .style("visibility", "hidden")
                .attr("pointer-events", "none");

            d3.selectAll("line.link-line-wide")
                .data(influentialLinks, (d) => `${d.source.id}-${d.target.id}`)
                .style("visibility", "hidden")
                .attr("pointer-events", "none");
        },

    }
}


// WEBPACK FOOTER //
// ./src/mixins/graphMixin.js